import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "react-modal";

import { User, UserSupportDocument } from "pwapi";

import { getSupportDocument } from "shared/actions/caseDetailActions";
import { addToast, nextId } from "shared/actions/toastActions";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { downloadDocument } from "shared/utils/documentUtils";

import { useAppDispatch } from "store/hooks";

import { DialogEnum } from "./Dialog";

type HelpDialogProps = {
  dialog?: DialogEnum | null;
  setDialog: Function;
  features: FeatureFlags;
  user?: User;
};

const HelpDialog = ({ dialog, setDialog, features, user }: HelpDialogProps) => {
  const dispatch = useAppDispatch();

  const onClickSupportDocument = (
    documentType: UserSupportDocument.document_type
  ) => {
    dispatch(getSupportDocument({ documentType })).then((response) => {
      if (!response.document_url) {
        dispatch(
          addToast(
            {
              kind: "danger",
              title: "Document Missing",
              message:
                "If this problem persists, please contact support@pathologywatch.com",
            },
            nextId()
          )
        );
      }

      if (response.extension !== ".pdf") {
        downloadDocument(
          response.document_url,
          response.name || "dot help document",
          response.extension
        );
      } else {
        window.open(response.document_url, "_blank");
      }
    });
  };

  return (
    <Modal
      isOpen={dialog === DialogEnum.Hotkeys}
      onRequestClose={() => {
        setDialog(null);
      }}
      className="Modal HelpDialog"
      overlayClassName="Overlay"
      contentLabel="Hotkeys"
    >
      <h3>Help &amp; Support</h3>
      <div className="HelpDialog__links">
        {!features?.hideUserSupportDocuments && (
          <div>
            <button
              onClick={() =>
                onClickSupportDocument(
                  UserSupportDocument.document_type.REFERENCEGUIDE
                )
              }
            >
              <FontAwesomeIcon icon="book" />
              <strong>dot. Handbook</strong>
              <FontAwesomeIcon icon="arrow-up-right-from-square" />
            </button>

            <div>
              <button
                onClick={() =>
                  onClickSupportDocument(
                    UserSupportDocument.document_type.MACROCODES
                  )
                }
              >
                <FontAwesomeIcon icon="file-excel" />
                <strong>Full List of Macro Codes</strong>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>

              <button
                onClick={() =>
                  onClickSupportDocument(
                    UserSupportDocument.document_type.ICD10CODES
                  )
                }
              >
                <FontAwesomeIcon icon="file-excel" />
                <strong>Site-Specific ICD-10 Codes</strong>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>
            </div>

            <div>
              <button
                onClick={() =>
                  onClickSupportDocument(
                    UserSupportDocument.document_type.LABORDERSTANDARDS
                  )
                }
              >
                <FontAwesomeIcon icon="file-pdf" />
                <strong>PW Lab Order Standards</strong>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>

              <button
                onClick={() =>
                  onClickSupportDocument(
                    UserSupportDocument.document_type.ORDERSUMMARY
                  )
                }
              >
                <FontAwesomeIcon icon="file-pdf" />
                <strong>Pathology Orders Summary</strong>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>
            </div>
            <div>
              <button
                onClick={() =>
                  onClickSupportDocument(UserSupportDocument.document_type.FAQS)
                }
              >
                <FontAwesomeIcon icon="file-pdf" />
                <strong>PW FAQ</strong>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>

              <button
                onClick={() =>
                  onClickSupportDocument(
                    UserSupportDocument.document_type.CRITICALRESULTALERTS
                  )
                }
              >
                <FontAwesomeIcon icon="file-pdf" />
                <strong>PW Critical Result Alerts</strong>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>
            </div>
          </div>
        )}
        <div>
          <p>
            Need Help? Please email{" "}
            <a href="mailto:support@pathologywatch.com">
              support@pathologywatch.com
            </a>
          </p>
        </div>
      </div>

      <h4>Viewer Hotkeys</h4>
      <div className="HelpDialog__hotkeys">
        <div>
          <table>
            <tr>
              <td>
                <strong>R</strong> or <strong>Z</strong>
              </td>
              <td>Rotate Left 10&deg;</td>
            </tr>
            <tr>
              <td>
                <strong>Shift+R</strong> or <strong>C</strong>
              </td>
              <td>Rotate Left 90&deg;</td>
            </tr>
            <tr>
              <td>
                <strong>T</strong> or <strong>X</strong>
              </td>
              <td>Rotate Right 10&deg;</td>
            </tr>
            <tr>
              <td>
                <strong>Shift+T</strong> or <strong>V</strong>
              </td>
              <td>Rotate Right 90&deg;</td>
            </tr>
            <tr>
              <td>
                <strong>-</strong>
              </td>
              <td>Zoom Out</td>
            </tr>
            <tr>
              <td>
                <strong>+</strong>
              </td>
              <td>Zoom In</td>
            </tr>
            <tr>
              <td>
                <strong>&lt;</strong> or <strong>F</strong>
              </td>
              <td>Previous Slide</td>
            </tr>
            <tr>
              <td>
                <strong>&gt;</strong> or <strong>G</strong>
              </td>
              <td>Next Slide</td>
            </tr>
            <tr>
              <td>
                <strong>Shift+[</strong>
              </td>
              <td>Previous Specimen</td>
            </tr>
            <tr>
              <td>
                <strong>Shift+]</strong>
              </td>
              <td>Next Specimen</td>
            </tr>
            <tr>
              <td>
                <strong>P</strong>
              </td>
              <td>Previous Case</td>
            </tr>
            <tr>
              <td>
                <strong>N</strong>
              </td>
              <td>Next Case</td>
            </tr>
          </table>
        </div>
        <div>
          <table>
            <tr>
              <td>
                <strong>Escape</strong>
              </td>
              <td>Cancel (Measurement or Drawing Mode)</td>
            </tr>
            <tr>
              <td>
                <strong>Return</strong>
              </td>
              <td>Complete Annotation (Drawing Mode)</td>
            </tr>
            <tr>
              <td>
                <strong>*</strong>
              </td>
              <td>Show/Hide Annotations</td>
            </tr>
            <tr>
              <td>
                <strong>S</strong>
              </td>
              <td>Toggle Specimen Information (if present)</td>
            </tr>
            {(user?.user_type === "dermpath" || user?.is_superuser) && (
              <tr>
                <td>
                  <strong>E</strong>
                </td>
                <td>Show Clinic/Physician Preferences (if present)</td>
              </tr>
            )}
            <tr>
              <td>
                <strong>D</strong>
              </td>
              <td>Toggle Diagnosis Dialog (if present)</td>
            </tr>
            {features.aiOverlay && (
              <tr>
                <td>
                  <strong>A</strong>
                </td>
                <td>Toggle AI Overlay (if present)</td>
              </tr>
            )}
            {features.stainOrdering && (
              <tr>
                <td>
                  <strong>O</strong>
                </td>
                <td>Toggle Order Panel (if present)</td>
              </tr>
            )}
            {features.caseFinalizeKey && (
              <tr>
                <td>
                  <strong>Shift-F</strong>
                </td>
                <td>Mark Case as Finalized</td>
              </tr>
            )}
            {features.dictation && (
              <tr>
                <td>
                  <strong>Ctrl+I</strong>
                </td>
                <td>Toggle Dictation</td>
              </tr>
            )}
            <tr>
              <td>
                <strong>H</strong>
              </td>
              <td>Toggle This Dialog</td>
            </tr>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default HelpDialog;
